<template>
    <base-panel
        :panel="panel"
        :review-mode="reviewMode"
        v-on:continue="handleContinue()"
        v-on:submit="handleSubmit()"
        v-on:edit-action="handleEditAction($event)"
        v-on:update-width="handleUpdateWidth($event)"
        ref="basePanel"
    >
        <template slot="content">
            <div>
                <div class="d-flex flex-column align-items-center my-4">
                    <!-- Microphone Controls & Timer -->
                    <div v-if="!reviewMode" class="d-flex flex-column align-items-center">
                        <div class="h5 font-weight-500 text-secondary record-margin">
                            {{ timeRemainingFormatted }}
                        </div>
                        <button
                            class="btn btn-danger px-2 py-2 rounded-circle d-flex align-items-center justify-content-center record-margin record-btn"
                            :class="{
                                'pulse-sm-danger': !isRecording && !recordingBlob && !locked && !isSubmitted,
                            }"
                            :disabled="recordingBlob || locked || isSubmitted || micIsLoading"
                            @click="startStopRecording()"
                        >
                            <div class="d-flex flex-column justify-content-center record-btn-icon">
                                <span
                                    class="icon"
                                    :class="{
                                        'icon-glyphicons-basic-mic': !isRecording,
                                        'icon-glyphicons-basic-stop': isRecording,
                                    }"
                                ></span>
                            </div>
                        </button>
                        <div v-if="micIsLoading">
                            <div class="d-flex align-items-center">
                                <div class="text-secondary">Initializing voice recorder...</div>
                            </div>
                        </div>
                        <div v-else-if="isRecording">
                            <div class="d-flex align-items-center">
                                <div class="unexplored-marker rounded-circle mr-2"></div>
                                <div class="text-secondary">Recording</div>
                            </div>
                        </div>
                    </div>
                    <!-- Action Text -->
                    <div v-show="!isRecording && !reviewMode && !micIsLoading">
                        <div class="d-flex flex-column align-items-center">
                            <div class="text-secondary text-center mb-0">
                                <span v-if="recordingBlob || isSubmitted || locked"
                                    >{{ labelsTranslated.recordingReview }}:</span
                                >
                                <span v-else>{{ labelsTranslated.recordingMicrophoneTap }}</span>
                            </div>
                        </div>
                    </div>
                    <!-- Recording Review -->
                    <div v-show="recordingBlob || savedResponse" :class="{ 'mt-4': !reviewMode }">
                        <div class="d-flex justify-content-center align-items-center">
                            <div class="mr-3 d-flex align-items-center">
                                <audio ref="audioPlayback" controls></audio>
                            </div>
                            <div v-if="!reviewMode" class="d-flex align-items-center">
                                <button
                                    v-show="recordingBlob && !isRecording && !submitIsAnalyzing && !isSubmitted"
                                    class="rounded-circle btn btn-gray review-btn px-0 py-0 mr-2"
                                    @click="clearRecording()"
                                >
                                    <span class="icon icon-glyphicons-basic-bin review-btn-icon"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Analyzing Badge -->
                <div v-if="submitIsAnalyzing" class="ml-1 small d-flex justify-content-center">
                    <span class="font-weight-500 flex-grow-0 text-light-wave"
                        >{{ labelsTranslated.analyzingYourResponse }}...</span
                    >
                </div>
                <!-- Transcription -->
                <div v-if="savedResponse && savedResponse.transcribed_text" class="pt-2 pb-1">
                    <h2 class="h6">{{ labelsTranslated.yourResponse }}:</h2>
                    <div class="text-secondary border-left quote-border">"{{ savedResponse.transcribed_text }}"</div>
                </div>
                <div v-if="savedResponseHasAiFeedback" class="mt-2">
                    <div class="collapse" :id="'feedback-' + _uid" :class="{ show: forceShowFeedback }">
                        <div class="mt-3">
                            <text-response-ai-feedback
                                :panel="panel"
                                :response="savedResponse"
                            ></text-response-ai-feedback>
                        </div>
                        <div
                            v-if="savedResponse.expert_response"
                            class="alert alert-primary border-0 mb-0 mt-4"
                            ref="instructorResponse"
                        >
                            <div class="font-weight-500 mb-1">{{ labelsTranslated.compareYourAnswer }}:</div>
                            <div style="white-space: pre-wrap">{{ savedResponse.expert_response }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </base-panel>
</template>

<script>
import Utilities from "@/assets/js/utilities.js"
import BasePanel from "@/components/panels/BasePanel"
import TextResponseAiFeedback from "@/components/TextResponseAiFeedback"

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => {
            // Get the Base64 string and remove 'data:audio/wav;base64,' prefix.
            let base64String = reader.result
            base64String = base64String.split(",")[1]
            resolve(base64String)
        }
        reader.onerror = reject
        reader.readAsDataURL(blob)
    })
}

export default {
    name: "VoiceRecordingPanel",
    components: {
        BasePanel,
        TextResponseAiFeedback,
    },
    props: {
        panel: {
            type: Object,
            required: true,
        },
        locked: {
            type: Boolean,
            required: false,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        reviewMode: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            isRecording: false,
            recordingBlob: null,
            recordingStream: null,
            recordingMimeType: "audio/webm",
            recordedChunks: [],
            base64EncodedRecording: null,
            mediaRecorder: null,
            recordingSecondsElapsed: 0,
            timeoutId: null,
            forceShowFeedback: false,
            micIsLoading: false,
        }
    },
    computed: {
        secondsRemaining() {
            return Math.max(this.panel.recording_time_limit_sec - this.recordingSecondsElapsed, 0)
        },
        timeRemainingFormatted() {
            return Utilities.formatStopwatch(this.secondsRemaining)
        },
        currentSession() {
            return this.$store.getters.session
        },
        showSubheader() {
            return this.panel.sub_header !== "" && this.panel.sub_header != "<p></p>"
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        submitIsAnalyzing() {
            return this.$store.getters.submitIsLoading
        },
        savedResponse() {
            if (!this.savedResponses || this.savedResponses.length == 0) {
                return null
            }
            return this.savedResponses[this.savedResponses.length - 1]
        },
        savedResponseHasAiFeedback() {
            return this.savedResponse && JSON.stringify(this.savedResponse.ai_gen_feedback) !== "{}"
        },
        isSubmitted() {
            return this.savedResponse
        },
    },
    watch: {
        isSubmitted(value) {
            this.$emit("update:isSubmitted", value)
            if (value) {
                this.$nextTick(() => {
                    $(`#feedback-${this._uid}`).collapse("show")
                })
            }
        },
    },
    methods: {
        // Start recording via the microphone.
        startRecording() {
            this.micIsLoading = true
            let vm = this
            const startRecordingCb = function (stream) {
                // Start the countdown timer.
                vm.startRecordingTimeout()
                vm.recordingStream = stream
                vm.isRecording = true

                // Determine which mimeType to use depending on the current browser.
                if (!MediaRecorder.isTypeSupported("audio/webm")) {
                    // This mimetype seems to work in Safari.
                    vm.recordingMimeType = "audio/mp4"
                }

                // Create the media recorder.
                const options = { mimeType: vm.recordingMimeType }
                const mediaRecorder = new MediaRecorder(vm.recordingStream, options)
                vm.recordedChunks = []
                vm.mediaRecorder = mediaRecorder

                // Store recording data as it becomes available.
                mediaRecorder.addEventListener("dataavailable", function (e) {
                    if (e.data.size > 0) vm.recordedChunks.push(e.data)
                })

                // Handle the recorder being stopped.
                mediaRecorder.addEventListener("stop", async function () {
                    vm.handleStopRecording()
                })

                // Start recording. If we don't pass in the optional 'timeslice' argument,
                // voice recordings created using Safari will fail to be transcribed properly.
                mediaRecorder.start(1000)
            }
            // Request to use the user's microphone - this will result in a prompt to allow
            // or disallow.
            navigator.mediaDevices
                .getUserMedia({
                    audio: {
                        noiseSuppression: true,
                        echoCancellation: true,
                        autoGainControl: true,
                    },
                    video: false,
                })
                .then(startRecordingCb)
                .finally(() => {
                    vm.micIsLoading = false
                })
        },
        // Stop recording.
        stopRecording() {
            if (this.mediaRecorder.state === "inactive") {
                return
            }
            this.mediaRecorder.stop()
            clearTimeout(this.timeoutId)
        },
        // Handle the recorder being stopped.
        async handleStopRecording() {
            // Stop the stream - this makes sure that the mic is disengaged in
            // the user's browser.
            let vm = this
            vm.recordingStream.getTracks().forEach((track) => track.stop())

            vm.isRecording = false

            // Create a blob using the recording and store it in memory for uploading
            // later on.
            vm.recordingBlob = new Blob(vm.recordedChunks, { type: vm.recordingMimeType })
            vm.base64EncodedRecording = await blobToBase64(vm.recordingBlob)

            vm.$emit("update:voiceResponse", {
                b64EncodedRecording: vm.base64EncodedRecording,
                mimeType: vm.recordingMimeType,
            })

            // Make sure the user can listen to their recording by attaching it to the
            // audio playback element.
            if (vm.$refs.audioPlayback) {
                vm.$refs.audioPlayback.src = URL.createObjectURL(vm.recordingBlob)
            }
            return new Promise((resolve) => {
                resolve()
            })
        },
        // Start the countdown timer loop.
        startRecordingTimeout() {
            let vm = this
            this.timeoutId = window.setTimeout(function () {
                // If remaining time has reached zero, stop the recording automatically.
                if (vm.secondsRemaining <= 0) {
                    vm.stopRecording()
                    return
                }
                vm.recordingSecondsElapsed++
                vm.startRecordingTimeout()
            }, 1000)
        },
        // Toggle the recorder on or off.
        startStopRecording() {
            if (this.isRecording) {
                this.stopRecording()
            } else {
                this.startRecording()
            }
        },
        // Clear the current recording.
        clearRecording() {
            this.recordingBlob = null
            this.recordingSecondsElapsed = 0
            this.$emit("update:voiceResponse", null)
        },
        handleContinue() {
            this.$emit("continue")
        },
        handleSubmit() {
            this.$emit("submit")
        },
        handleEditAction(action) {
            this.$emit("edit-action", action)
        },
        handleUpdateWidth($event) {
            this.$emit("update-width", $event)
        },
        skipVideos() {
            this.$refs.basePanel.skipVideos()
        },
    },
    destroyed() {
        // Make sure the countdown timeout is removed.
        if (this.timeoutId) {
            clearTimeout(this.timeoutId)
        }
    },
    mounted() {
        if (this.savedResponses && this.savedResponses.length) {
            let vm = this
            this.$nextTick(() => {
                // Allow the learner to listen to the previously saved recording.
                vm.$refs.audioPlayback.src = this.savedResponses[0].stored_file.signed_url
            })
        }
        if (this.savedResponse) {
            this.forceShowFeedback = true
        }
        this.$emit("update:isSubmitted", this.isSubmitted)
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/hotspots.scss";

.unexplored-marker {
    width: 10px;
    height: 10px;
    background-color: $danger;
    animation: 1.5s ease-in-out 0s infinite normal both running unex-pulse;
}

@keyframes unex-pulse {
    0% {
        transform: scale(1.05);
    }
    50% {
        transform: scale(1.23);
    }
    100% {
        transform: scale(1.05);
    }
}

.record-btn {
    height: 60px;
    width: 60px;
}

.record-btn-icon {
    font-size: 30px;
}

.review-btn {
    height: 38px;
    width: 38px;
}

.review-btn-icon {
    font-size: 21px !important;
}

.record-margin {
    margin-bottom: 1.4rem;
}
</style>